import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { AlertTriangle, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/errors";

/**
 * AIStatusBanner
 * Polls the AI circuit-breaker state. When AI is paused (budget cap hit),
 * shows a top-of-page banner with a countdown and a "Reset AI" button.
 */
export default function AIStatusBanner() {
  const [status, setStatus] = useState(null); // { paused, seconds_remaining }
  const [resetting, setResetting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/ai/status");
      setStatus(data);
    } catch {
      // silent — banner is optional UX
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000); // poll every 15s
    return () => clearInterval(t);
  }, []);

  // Local countdown so the displayed time decrements even between polls
  useEffect(() => {
    if (!status?.paused) return;
    const t = setInterval(() => {
      setStatus((s) =>
        s && s.paused && s.seconds_remaining > 0
          ? { ...s, seconds_remaining: s.seconds_remaining - 1 }
          : s
      );
    }, 1000);
    return () => clearInterval(t);
  }, [status?.paused]);

  if (!status?.paused) return null;

  const mins = Math.max(0, Math.ceil((status.seconds_remaining || 0) / 60));

  const doReset = async () => {
    setResetting(true);
    try {
      await api.post("/ai/reset");
      await load();
      toast.success("AI features re-enabled. Give it a try.");
    } catch (e) {
      toast.error(formatApiError(e, "Could not reset AI"));
    } finally {
      setResetting(false);
    }
  };

  return (
    <div
      className="bg-rose-50 border-b border-rose-200 px-4 sm:px-6 py-2.5 flex items-center gap-3"
      data-testid="ai-status-banner"
    >
      <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-rose-800">
          AI features paused — budget cap hit
        </div>
        <div className="text-xs text-rose-700/80">
          Pausing for ~{mins} more minute{mins === 1 ? "" : "s"} so we don't
          burn credit on retries. Email{" "}
          <a
            href="mailto:support@emergent.sh"
            className="underline font-medium"
          >
            support@emergent.sh
          </a>{" "}
          to raise your gateway cap, then click Reset to resume.
        </div>
      </div>
      <button
        onClick={doReset}
        disabled={resetting}
        className="inline-flex items-center gap-1.5 rounded-sm bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-xs font-medium px-3 py-1.5"
        data-testid="ai-status-reset-btn"
      >
        {resetting ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <RotateCcw className="w-3.5 h-3.5" />
        )}
        Reset AI
      </button>
    </div>
  );
}
